import React, { useEffect, useRef, useState } from 'react';
import './ShareViewer.css';
import TopicChapterStrip from './TopicChapterStrip';
import VideoOutageOverlay from './VideoOutageOverlay';
import { useVideoOutageDetect } from '../utils/videoOutageDetect';
import { whenHuman } from '../utils/humanPresence';

/**
 * ShareViewer — the public landing page rendered at /share/:token.
 *
 * Intentionally isolated from the rest of App.jsx's auth/dashboard state.
 * This is a completely different surface: no login, no sidebar, no theme
 * toggle — just a branded player with seller context and a few analytics
 * beacons firing back to the API. Anyone with the link can watch.
 *
 * Fetches:
 *   GET  /api/trailers/meta/:token   → metadata for the page
 *   GET  /api/trailers/stream/:token → video source for <video>
 *   POST /api/trailers/view/:token   → fire-and-forget beacons
 */

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

// How often to send a `progress` beacon (seconds of wall-clock, not video time).
// Every 15s is the sweet spot — frequent enough to detect drop-off, infrequent
// enough not to hammer the endpoint.
const PROGRESS_BEACON_INTERVAL_MS = 15_000;

function formatClipTime(seconds) {
  if (seconds == null || !Number.isFinite(seconds)) return '';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Compact "time ago" formatter for the forwarded-teammates list so the
// champion can see at-a-glance which of their forwards have actually
// opened the link — the engagement signal that drives follow-up
// prioritization for the seller.
function formatTimeAgo(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  const diffWk = Math.floor(diffDay / 7);
  if (diffWk < 5) return `${diffWk} wk${diffWk === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Lightweight email sanity check — client-side only to give immediate
// feedback; the server does the real validation on the forward endpoint.
function looksLikeEmail(value) {
  if (typeof value !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

// Build a mailto: URL for one-click "Open in email" forwarding. Using
// mailto: instead of sending through our own SMTP keeps the email
// originating from the champion's REAL inbox — better deliverability
// to C-suite recipients (who auto-trash unknown-domain mail) and
// natural threading on replies. Falls back to a no-recipient mailto
// if the recipient email is missing for any reason.
function buildMailtoUrl({ to, subject, body }) {
  const enc = (s) => encodeURIComponent(String(s == null ? '' : s));
  const target = to ? enc(to) : '';
  const params = [];
  if (subject) params.push(`subject=${enc(subject)}`);
  if (body) params.push(`body=${enc(body)}`);
  return `mailto:${target}${params.length ? `?${params.join('&')}` : ''}`;
}

// `embedded` (default false): when true, ShareViewer is being rendered
// inside another shell (e.g., the Decision Room prospect view's Demos
// section). In that mode we drop our outer page chrome — brand bar,
// the "Prepared by" header, the forwarded-from banner — because the
// host shell already provides equivalent branding + identity context.
// The video player + chapters + chat thread (the actual content) stay.
// Playback speeds the player-bar button cycles through. Native <video>
// controls also expose a rate menu on some browsers; this button makes
// it one predictable click everywhere (and keyboard/screen-reader
// reachable) without replacing the native control strip.
const PLAYBACK_RATES = [1, 1.25, 1.5, 1.75, 2];

export default function ShareViewer({ token, embedded = false }) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // When the share's demo exists but isn't 'completed' yet, the meta
  // endpoint 400s with { error, status }. We capture that status so the
  // page can render a "still generating" state (with auto-repoll) rather
  // than the dead-link error card. Null = not in processing state.
  const [processingStatus, setProcessingStatus] = useState(null);
  // Player-bar speed control. Applied to the <video> via effect so it
  // survives src reloads (e.g. after an outage retry remounts the src).
  const [playbackRate, setPlaybackRate] = useState(1);
  // Tracked once the <video> element reports loadedmetadata. Used to clamp
  // chapter-jump targets to within the trailer (older demos may not have
  // reelOffsetSeconds — we fall back gracefully instead of seeking past end)
  // and to size the timeline-chapter-marker bar proportionally.
  const [videoDuration, setVideoDuration] = useState(0);

  // Engagement form state — Q&A / comment / follow-up request that the
  // prospect can send straight to the seller. One form, three kinds via
  // a segmented-button selector. Pre-filled from personalized-link meta
  // when available (stakeholderName/Email).
  const [messageKind, setMessageKind] = useState('question');
  const [messageName, setMessageName] = useState('');
  const [messageEmail, setMessageEmail] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [messageHoneypot, setMessageHoneypot] = useState(''); // must stay empty
  const [messageSubmitting, setMessageSubmitting] = useState(false);
  const [messageError, setMessageError] = useState(null);
  const [messageSent, setMessageSent] = useState(false);
  // V4 (2026-04-27): clip reference. The prospect can tag a message
  // with "I'm asking about clip N" so the seller's inbox renders
  // "Re: <feature>" alongside the message. Defaults to the moment
  // the playhead is currently over (auto-tracked via the
  // playheadMomentIndex effect below); user can override via the
  // dropdown or pick "General" to send untagged. null = General.
  const [referencedClipIndex, setReferencedClipIndex] = useState(null);
  // Auto-tracked: which moment the video playhead is currently over.
  // Recomputed on timeupdate. We use this to PRESELECT the clip
  // dropdown but never to overwrite the user's explicit choice.
  const [playheadMomentIndex, setPlayheadMomentIndex] = useState(null);
  // Whether the user has explicitly touched the dropdown — once they
  // have, we stop syncing it to the playhead so their pick sticks.
  const [clipPickTouched, setClipPickTouched] = useState(false);
  // V2: thread resume mode. When the prospect clicks the magic link
  // in a seller-reply email, the URL carries `?t=<threadKey>&s=<sig>`.
  // We fetch the thread and render the full convo inline above the
  // form; the form becomes a "reply" box locked to the same email.
  const [threadKey, setThreadKey] = useState(null);
  const [threadSig, setThreadSig] = useState(null);
  const [threadMessages, setThreadMessages] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);
  // When an authed seller opens the share page via the email deep-link,
  // the backend returns isOwner=true on the thread endpoint. This flag
  // swaps the prospect engagement form for a seller-reply form that
  // hits the authed endpoint, so the share page becomes a true
  // communication hub for both sides.
  const [threadIsOwner, setThreadIsOwner] = useState(false);
  const [threadDemoId, setThreadDemoId] = useState(null);
  const [sellerReplyBody, setSellerReplyBody] = useState('');
  const [sellerReplySubmitting, setSellerReplySubmitting] = useState(false);
  const [sellerReplyError, setSellerReplyError] = useState(null);

  // Champion-forwarding state. The sales-engagement story: a trailer viewer
  // (the "champion") fans the demo out to internal stakeholders (CFO,
  // CISO, etc.) and we mint a unique tracked link per teammate so the
  // seller sees WHO inside the buying committee actually watched. This
  // is the core of multi-threaded deal engagement.
  const [forwardFirstName, setForwardFirstName] = useState('');
  const [forwardEmail, setForwardEmail] = useState('');
  const [forwardNote, setForwardNote] = useState('');
  const [forwardSubmitting, setForwardSubmitting] = useState(false);
  const [forwardError, setForwardError] = useState(null);
  const [forwardResult, setForwardResult] = useState(null); // { url, preWrittenMessage, recipientName, reused }
  const [forwardCopied, setForwardCopied] = useState({ url: false, message: false });

  // Phase 2D: source-recording deep-link modal. Clicking "Watch full
  // segment" on a chapter opens this modal with the source-stream video
  // seeked to the timestamp where the trailer's clip ended — gives the
  // prospect the rest of the original segment on demand.
  const [sourceModalAt, setSourceModalAt] = useState(null); // number (seconds) or null
  const [sourceDuration, setSourceDuration] = useState(0); // filled from onLoadedMetadata so the header can show "X of TOTAL" — also helps confirm to the viewer that it IS the full recording (duration should be much longer than the trailer)
  const sourceVideoRef = useRef(null);

  const videoRef = useRef(null);
  // R2 outage detection — if Cloudflare R2 returns 503 + R2_OUTAGE for
  // either player, swap the broken-video frame for a polished overlay
  // explaining the transient incident + offering retry. Critical on this
  // surface because it's prospect-facing — a black frame reads as "this
  // seller's product is broken." See utils/videoOutageDetect.js header.
  const trailerUrl = `${API_BASE}/api/trailers/stream/${token}`;
  const sourceUrl = `${API_BASE}/api/trailers/source-stream/${token}`;
  // rooms_any_recording (2026-08-28): a meeting or qualifying call has
  // no composed trailer — the main video IS the source recording. The
  // meta says so via trailerAvailable; older backends omit the field
  // (undefined !== false), so legacy behavior is untouched.
  const mainVideoUrl = (meta && meta.trailerAvailable === false && meta.sourceAvailable)
    ? sourceUrl
    : trailerUrl;
  const trailerOutage = useVideoOutageDetect(videoRef, mainVideoUrl);
  const sourceOutage = useVideoOutageDetect(sourceVideoRef, sourceUrl);
  const lastProgressBeaconAt = useRef(0);
  // Guard so the 'view' beacon only fires once per page load even if
  // React StrictMode double-invokes effects in development.
  const viewBeaconFired = useRef(false);

  // Session id for engagement heat-map segment events. One ID per
  // page-load so rewatches in the same tab all attribute to the
  // same session, and the heatmap can tell "three viewers each
  // watched pricing once" from "one viewer rewatched pricing three
  // times." Crypto.randomUUID is widely available; fall back to a
  // timestamp+random combo for exotic browsers.
  const heatmapSessionId = useRef(
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  );

  // Tracks the last position we emitted a segment for, so the
  // playback segment-emitter below can compute the [from, to] range
  // covered since the previous heartbeat. Resets on seek so a jump
  // from 20s to 80s doesn't write a bogus [20, 80] segment.
  const lastSegmentPos = useRef({ at: null, clock: null });

  // Fire-and-forget beacon. Uses sendBeacon when available (survives page
  // unload) and falls back to fetch otherwise.
  //
  // 2026-04-26 fix: ship the body as Blob type 'text/plain' instead of
  // 'application/json'. The latter is a "non-simple" CORS request that
  // forces a preflight; once cross-origin, the preflight + the actual
  // ping have to BOTH satisfy CORS or the browser drops the ping with
  // a generic "CORS error" in DevTools (and no request reaches the
  // server). text/plain is on the simple-request whitelist — no
  // preflight, no CORS gymnastics. The backend treats the body as a
  // string and JSON.parses it server-side.
  const beacon = (event, extras = {}) => {
    if (!token) return;
    // Deferred until a real input event (2026-08-25) — see
    // utils/humanPresence.js. Mail-filter detonation sandboxes run
    // this page in headless Chrome, and the on-load 'view' beacon was
    // flipping "Not yet opened" → engaged for mail sitting in
    // quarantine. Beacons queue behind the first human input; in an
    // automated session nothing sends. Play/seek/segment events all
    // require a click anyway, so only the initial 'view' changes
    // behaviour.
    whenHuman(() => {
    const url = `${API_BASE}/api/trailers/view/${token}`;
    const body = JSON.stringify({ event, ...extras });
    try {
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
        navigator.sendBeacon(url, blob);
        return;
      }
    } catch (_) {
      // sendBeacon can throw in exotic browsers — fall through to fetch.
    }
    fetch(url, {
      method: 'POST',
      // text/plain matches the sendBeacon path so the backend's body
      // parsing only has to handle one shape.
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body,
      keepalive: true,
    }).catch(() => { /* swallow — analytics must never break playback */ });
    });
  };

  // Load metadata on mount; fire the initial 'view' beacon once loaded.
  // Re-runs while the trailer is still processing (see the repoll effect
  // below) so the page flips to the player the moment compose finishes.
  const loadMeta = (opts = {}) => {
    let cancelled = false;
    if (!opts.silent) setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/trailers/meta/${token}`)
      .then(async (r) => {
        if (!r.ok) {
          const payload = await r.json().catch(() => ({}));
          // "Trailer is not ready yet" ships the demo's live status —
          // that's a processing state, not a dead link. Surface it as
          // such instead of the generic error card.
          if (r.status === 400 && payload.status && payload.status !== 'completed') {
            const e = new Error(payload.error || 'Trailer is not ready yet');
            e.processingStatus = payload.status;
            throw e;
          }
          throw new Error(payload.error || `Failed to load share (status ${r.status})`);
        }
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setProcessingStatus(null);
        setMeta(data);
        setLoading(false);
        if (!viewBeaconFired.current) {
          viewBeaconFired.current = true;
          beacon('view');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.processingStatus) {
          setProcessingStatus(err.processingStatus);
        } else {
          setError(err.message);
        }
        setLoading(false);
      });
    return () => { cancelled = true; };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => loadMeta(), [token]);

  // While processing, quietly re-check every 10s so the viewer doesn't
  // have to refresh — the page swaps to the player as soon as the
  // trailer completes. Silent so the spinner screen doesn't flicker.
  useEffect(() => {
    if (!processingStatus) return undefined;
    const t = setInterval(() => loadMeta({ silent: true }), 10_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingStatus]);

  // Keep the <video>'s playbackRate in sync with the player-bar button —
  // reapplied via loadedmetadata so a src remount (outage retry) doesn't
  // silently reset the viewer's chosen speed.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    v.playbackRate = playbackRate;
    const reapply = () => { v.playbackRate = playbackRate; };
    v.addEventListener('loadedmetadata', reapply);
    return () => v.removeEventListener('loadedmetadata', reapply);
  }, [playbackRate, meta]);

  const cyclePlaybackRate = () => {
    const idx = PLAYBACK_RATES.indexOf(playbackRate);
    setPlaybackRate(PLAYBACK_RATES[(idx + 1) % PLAYBACK_RATES.length]);
  };

  // Wire up video event listeners for progress + completion beacons.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !meta) return;

    // Segment emitter — fires every ~5s of continuous playback with
    // { fromSeconds, toSeconds } describing the reel-time range
    // covered since the last emission. Feeds the engagement heatmap.
    const SEGMENT_INTERVAL_MS = 5000;
    let segmentTimer = null;
    const emitSegmentIfPlaying = () => {
      if (video.paused || video.ended) return;
      const nowClock = Date.now();
      const pos = video.currentTime || 0;
      const last = lastSegmentPos.current;
      if (last.at != null && last.clock != null) {
        const elapsedSec = (nowClock - last.clock) / 1000;
        const reelDelta = pos - last.at;
        // Only emit when playback progressed approximately linearly
        // (reel-time delta ≈ wall-clock delta within a 20% tolerance).
        // A big reel-delta vs small wall-clock-delta means a seek
        // happened — discard that as a segment.
        if (reelDelta > 0 && Math.abs(reelDelta - elapsedSec) < Math.max(1.5, elapsedSec * 0.4)) {
          beacon('segment', {
            sessionId: heatmapSessionId.current,
            fromSeconds: Math.max(0, last.at),
            toSeconds: Math.max(last.at + 0.1, pos),
          });
        }
      }
      lastSegmentPos.current = { at: pos, clock: nowClock };
    };

    const onPlay = () => {
      // Reset the "from" anchor so the first segment after a resume
      // spans from the current position, not where we paused.
      lastSegmentPos.current = { at: video.currentTime || 0, clock: Date.now() };
      if (segmentTimer) clearInterval(segmentTimer);
      segmentTimer = setInterval(emitSegmentIfPlaying, SEGMENT_INTERVAL_MS);
    };
    const onPauseOrEnded = () => {
      // Emit one final segment on pause so a short watch isn't lost.
      emitSegmentIfPlaying();
      if (segmentTimer) {
        clearInterval(segmentTimer);
        segmentTimer = null;
      }
      // Invalidate the anchor so the next onPlay reseats it.
      lastSegmentPos.current = { at: null, clock: null };
    };
    const onSeeking = () => {
      // Seeks break continuity — drop the anchor without emitting so
      // a big jump doesn't write [small, big] as "watched".
      lastSegmentPos.current = { at: null, clock: null };
    };

    const onTimeUpdate = () => {
      const now = Date.now();
      if (now - lastProgressBeaconAt.current < PROGRESS_BEACON_INTERVAL_MS) return;
      lastProgressBeaconAt.current = now;
      const watchSeconds = Math.round(video.currentTime || 0);
      const durationSeconds = Math.round(video.duration || 0);
      const completionPct = durationSeconds > 0
        ? Math.round((watchSeconds / durationSeconds) * 100)
        : null;
      beacon('progress', { watchSeconds, completionPct });
    };

    const onEnded = () => {
      onPauseOrEnded();
      beacon('complete', {
        watchSeconds: Math.round(video.duration || 0),
        completionPct: 100,
      });
    };

    const onLoadedMetadata = () => {
      setVideoDuration(video.duration || 0);
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', onEnded);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPauseOrEnded);
    video.addEventListener('seeking', onSeeking);
    // If metadata already loaded before this listener attached (e.g. cached)
    if (video.readyState >= 1 && video.duration) setVideoDuration(video.duration);
    return () => {
      if (segmentTimer) clearInterval(segmentTimer);
      // Final flush on unmount so an abandoned tab still contributes
      // its last few seconds of watch data to the heat-map.
      try { emitSegmentIfPlaying(); } catch (_) { /* noop */ }
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPauseOrEnded);
      video.removeEventListener('seeking', onSeeking);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  // Pick the best seek target for a moment: prefer reelOffsetSeconds (time
  // within the trailer) if present; otherwise fall back to startSeconds clamped
  // to video.duration so the player doesn't snap past the end for older
  // demos composed before reelOffsetSeconds existed.
  const seekTargetFor = (m) => {
    if (typeof m.reelOffsetSeconds === 'number' && Number.isFinite(m.reelOffsetSeconds)) {
      return Math.max(0, m.reelOffsetSeconds);
    }
    const fallback = typeof m.startSeconds === 'number' ? m.startSeconds : 0;
    if (videoDuration > 0) return Math.max(0, Math.min(fallback, videoDuration - 0.5));
    return Math.max(0, fallback);
  };

  // Jump to a specific moment in the player when the user clicks a
  // chapter in the table of contents or a timeline marker. Fires a
  // chapter_click beacon (2026-07-20) — stored in the unified
  // engagement log, never notified (too chatty), feeds the seller's
  // "which chapters did they navigate to" timeline.
  const jumpTo = (m, meta_ = {}) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = seekTargetFor(m);
    v.play().catch(() => { /* user-gesture policies — ignore */ });
    beacon('chapter_click', {
      atSeconds: seekTargetFor(m),
      ...(typeof meta_.index === 'number' ? { chapterIndex: meta_.index } : {}),
      ...(m.feature ? { chapterTitle: m.feature } : {}),
    });
  };

  // Track which moment the playhead is currently over so the message
  // form can preselect the right clip in the "Re:" dropdown. Lightweight
  // — the lookup is linear over a small array (typically 5-10 moments)
  // and runs only on timeupdate (~4 Hz).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const moments = Array.isArray(meta?.moments) ? meta.moments : null;
    if (!moments || moments.length === 0) return;
    const recompute = () => {
      const t = v.currentTime || 0;
      // Pick the moment whose reelOffsetSeconds is the largest value
      // <= currentTime. Falls back to startSeconds for legacy demos
      // that pre-date reelOffsetSeconds being baked in.
      let bestIdx = null;
      let bestStart = -1;
      for (let i = 0; i < moments.length; i++) {
        const m = moments[i];
        const start = typeof m.reelOffsetSeconds === 'number'
          ? m.reelOffsetSeconds
          : (typeof m.startSeconds === 'number' ? m.startSeconds : null);
        if (start == null) continue;
        if (start <= t && start > bestStart) {
          bestStart = start;
          bestIdx = i;
        }
      }
      setPlayheadMomentIndex(bestIdx);
    };
    v.addEventListener('timeupdate', recompute);
    recompute();
    return () => v.removeEventListener('timeupdate', recompute);
  }, [meta]);

  // Sync the form's clip pick to the playhead until the user
  // explicitly touches the dropdown — then their choice sticks.
  useEffect(() => {
    if (clipPickTouched) return;
    setReferencedClipIndex(playheadMomentIndex);
  }, [playheadMomentIndex, clipPickTouched]);

  const onCtaClick = () => {
    beacon('cta_click');
    // Let the native <a> navigation proceed.
  };

  // Prefill the engagement form. Two viewer modes to handle:
  //
  // 1. Prospect (default) — share link was sent to a specific
  //    person, prefill with their name + email from the
  //    distribution so they don't re-type.
  //
  // 2. Seller (room owner viewing their own link) — when the seller
  //    clicks the "Open conversation in TrailerCast" button in
  //    their notification email, they land on the prospect's
  //    share page. The seller-reply form takes over via
  //    threadIsOwner detection in fetchThread. BUT if that
  //    detection lags or fails (auth domain mismatch, expired
  //    token, etc.), the seller would otherwise see "Claudia's"
  //    name + email pre-filled in their reply form — confusing.
  //    To prevent that, we synchronously check if the viewer's
  //    auth-user matches meta.creatorUserId BEFORE the prospect
  //    prefill fires. If yes, prefill with the SELLER's name +
  //    email instead. Result: the seller always sees their own
  //    identity, regardless of whether the seller-reply form is
  //    fully wired up yet.
  useEffect(() => {
    if (!meta) return;
    let cancelled = false;
    (async () => {
      // Quick check for "am I the seller?" via /auth/me. Skipped
      // entirely when no auth token is present (most prospects).
      // Match on EITHER user.id===creatorUserId OR
      // user.email===creatorEmail — belt-and-suspenders so the
      // detection still works if user-id types diverge across
      // auth/me and the share-meta SELECT (one returns int, the
      // other could return string-cast-int through JSON), or if
      // creatorUserId is null on legacy distributions.
      let viewerIsSeller = false;
      let authUser = null;
      try {
        const tok = (typeof window !== 'undefined' && window.localStorage)
          ? window.localStorage.getItem('trailercst_token') : null;
        if (tok) {
          const res = await fetch(`${API_BASE}/api/auth/me`, {
            headers: { Authorization: `Bearer ${tok}` },
          });
          if (res.ok) {
            const body = await res.json();
            authUser = body && body.user;
            if (authUser) {
              const idMatch = meta.creatorUserId != null
                && String(authUser.id) === String(meta.creatorUserId);
              const emailMatch = meta.creatorEmail
                && authUser.email
                && authUser.email.toLowerCase() === String(meta.creatorEmail).toLowerCase();
              if (idMatch || emailMatch) {
                viewerIsSeller = true;
              }
            }
          }
        }
      } catch (_) { /* defensive — fall through to prospect prefill */ }

      if (cancelled) return;

      if (viewerIsSeller && authUser) {
        // Seller viewing their own link — prefill with their identity.
        // Unconditional set (no `!messageName` guard) so we OVERRIDE
        // any earlier prospect-side prefill if it slipped through.
        const fullName = (authUser.fullName || '').trim()
          || [authUser.firstName, authUser.lastName].filter(Boolean).join(' ').trim()
          || (authUser.email ? authUser.email.split('@')[0] : '');
        if (fullName) setMessageName(fullName);
        if (authUser.email) setMessageEmail(authUser.email);
        return;
      }
      // Default prospect-prefill path.
      if (meta.stakeholderName && !messageName) setMessageName(meta.stakeholderName);
      if (meta.stakeholderEmail && !messageEmail) setMessageEmail(meta.stakeholderEmail);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  // V2: if the URL carries a signed thread-resume token, capture it
  // and load the thread once the share meta resolves.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('t');
    const s = params.get('s');
    if (t && s) {
      setThreadKey(t);
      setThreadSig(s);
    }
  }, []);

  const fetchThread = async () => {
    if (!token) return;
    setThreadLoading(true);
    setThreadError(null);
    try {
      // Email-deeplink path passes t+s; auto-load path skips them
      // and the backend derives the thread identity from the token's
      // distribution. Either is a valid credential.
      const qs = (threadKey && threadSig)
        ? `?t=${encodeURIComponent(threadKey)}&s=${encodeURIComponent(threadSig)}`
        : '';
      const url = `${API_BASE}/api/trailers/thread/${token}${qs}`;
      // If a seller session is in localStorage (the app shares this
      // key with the main dashboard), forward it so the backend can
      // detect owner-of-demo and flip isOwner=true — which in turn
      // unlocks the seller-reply form below. Prospects without a
      // session just don't send this header; isOwner stays false.
      let authToken = null;
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          authToken = window.localStorage.getItem('trailercst_token');
        }
      } catch (_) { /* privacy mode — skip */ }
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : undefined;
      const res = await fetch(url, { headers });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Thread load failed (${res.status})`);
      }
      const data = await res.json();
      setThreadMessages(data.messages || []);
      setThreadIsOwner(Boolean(data.isOwner));
      setThreadDemoId(data.demoId || null);
      // Lock the form to the thread's email so the prospect's new
      // reply lands in the same thread.
      if (data.prospectEmail) setMessageEmail(data.prospectEmail);
      // Mirror the kind of the most recent prospect message for
      // analytics continuity.
      const lastProspect = [...(data.messages || [])].reverse().find((m) => m.senderKind === 'prospect');
      if (lastProspect && lastProspect.kind) setMessageKind(lastProspect.kind);
    } catch (err) {
      setThreadError(err.message);
      setThreadMessages([]);
    } finally {
      setThreadLoading(false);
    }
  };

  // Seller reply on the share page — only surfaced when the thread
  // endpoint flagged isOwner=true. Hits the authed reply endpoint so
  // the message gets sender_kind='seller' + seller_user_id + proper
  // thread_email routing.
  const submitSellerReply = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const body = sellerReplyBody.trim();
    if (!body) {
      setSellerReplyError('Type a reply before sending.');
      return;
    }
    if (!threadDemoId || !threadKey) {
      setSellerReplyError('Thread context unavailable. Refresh and try again.');
      return;
    }
    setSellerReplySubmitting(true);
    setSellerReplyError(null);
    try {
      const authToken = (typeof window !== 'undefined' && window.localStorage)
        ? window.localStorage.getItem('trailercst_token')
        : null;
      if (!authToken) throw new Error('Not signed in. Open the app dashboard first.');

      const url = `${API_BASE}/api/demos/${threadDemoId}/threads/${encodeURIComponent(threadKey)}/reply`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Reply failed (${res.status})`);
      }
      setSellerReplyBody('');
      // Re-fetch the thread to show the new message inline.
      fetchThread();
    } catch (err) {
      setSellerReplyError(err.message);
    } finally {
      setSellerReplySubmitting(false);
    }
  };

  // Submit a champion-forward: the viewer hands us a teammate's
  // first name + email (+ optional note) and we mint a fresh
  // personalized distribution link they can paste into their own
  // email. Re-paste from the champion's own inbox is intentional —
  // the social graph does the deliverability work for us.
  const submitForward = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const firstName = forwardFirstName.trim();
    const email = forwardEmail.trim();
    if (!firstName) {
      setForwardError("Enter your teammate's first name.");
      return;
    }
    if (!looksLikeEmail(email)) {
      setForwardError("That doesn't look like a valid email address.");
      return;
    }
    setForwardSubmitting(true);
    setForwardError(null);
    try {
      const res = await fetch(`${API_BASE}/api/trailers/share/${token}/forward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          email,
          note: forwardNote.trim() || undefined,
          // 'demo' forwards just this trailer + thread, not the whole
          // Decision Room. Backend allows this for everyone (including
          // members) since the demo carries less sensitive context than
          // the full room.
          forwardKind: 'demo',
        }),
      });
      if (res.status === 429) {
        throw new Error("You've forwarded too many trailers from this IP. Try again in a few minutes.");
      }
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || "Couldn't create the link. Try again.");
      }
      const data = await res.json();
      setForwardResult({
        url: data.url,
        preWrittenMessage: data.preWrittenMessage || '',
        recipientName: data.recipientName || firstName,
        // Captured so the "Open in email" mailto: link can prefill
        // the recipient. Backend echoes this back lowercased + trimmed
        // (matches the dedup index), but the UI uses whatever the
        // champion typed if the backend doesn't echo it.
        recipientEmail: data.recipientEmail || email,
        reused: Boolean(data.reused),
      });
    } catch (err) {
      // fetch() itself only throws on network failure; business errors
      // were thrown above. Distinguish so we show the right copy.
      if (err instanceof TypeError) {
        setForwardError("Couldn't create the link. Try again.");
      } else {
        setForwardError(err.message);
      }
    } finally {
      setForwardSubmitting(false);
    }
  };

  // Reset the forward form so the champion can send to another
  // teammate. We intentionally keep forwardedChildren fresh only on
  // next meta refresh — updating it optimistically would require
  // a full meta reload and the children list is a "nice to have,"
  // not the critical success surface (the copy-link panel is).
  const resetForwardForm = () => {
    setForwardResult(null);
    setForwardFirstName('');
    setForwardEmail('');
    setForwardNote('');
    setForwardError(null);
    setForwardCopied({ url: false, message: false });
  };

  // Copy-to-clipboard with a brief "Copied!" state. navigator.clipboard
  // is widely supported but we fall back to a hidden textarea trick for
  // older browsers / insecure contexts.
  const copyToClipboard = async (text, which) => {
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setForwardCopied((prev) => ({ ...prev, [which]: true }));
      setTimeout(() => {
        setForwardCopied((prev) => ({ ...prev, [which]: false }));
      }, 2000);
    } catch (_) {
      // Silent failure — user can still select + copy manually.
    }
  };

  /**
   * Delete one of the seller's own messages in this thread. Only
   * exposed when threadIsOwner is true (seller is viewing their own
   * thread via the email deep-link / preview mode). Backend enforces
   * the same — sender_kind='seller' + seller_user_id match — so a
   * crafted request from a prospect-side page 403s cleanly.
   */
  const deleteOwnMessage = async (messageId) => {
    if (!messageId) return;
    const ok = typeof window !== 'undefined'
      ? window.confirm('Delete this message? The prospect will no longer see it in the conversation. This cannot be undone.')
      : true;
    if (!ok) return;
    try {
      const authToken = (typeof window !== 'undefined' && window.localStorage)
        ? window.localStorage.getItem('trailercst_token')
        : null;
      if (!authToken) throw new Error('Not signed in.');
      const res = await fetch(`${API_BASE}/api/conversations/messages/${encodeURIComponent(messageId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || `Delete failed (${res.status})`);
      }
      // Optimistic: drop the message from local state. A background
      // fetchThread would also catch up, but local splice feels
      // instant + avoids a network round-trip flash.
      setThreadMessages((prev) =>
        Array.isArray(prev) ? prev.filter((m) => m.id !== messageId) : prev
      );
    } catch (err) {
      // Using alert rather than a local error banner — the button is
      // inline and deep; a banner at the top of the section would feel
      // disconnected from the action.
      if (typeof window !== 'undefined') {
        window.alert(`Couldn't delete message: ${err.message}`);
      }
    }
  };

  useEffect(() => {
    if (!meta) return;
    // Two trigger paths to render the thread inline:
    //   1. Email-deeplink: ?t=<key>&s=<sig> in the URL — opens the
    //      thread tied to that specific (distribution, email) pair.
    //   2. Token-only auto-load: prospect has stakeholderEmail on
    //      this distribution. Their token IS the credential, so we
    //      can load their thread without the signed URL params.
    //      Makes the share page feel like an iMessage thread —
    //      every prior message visible automatically when they
    //      come back to the link.
    // The backend handles both paths and returns the same shape.
    if ((threadKey && threadSig) || meta.stakeholderEmail) {
      fetchThread();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadKey, threadSig, meta]);

  // Phase 2D: open the source-recording modal at a specific timestamp.
  // Pauses the trailer so the two players don't audio-overlap.
  const openSourceAt = (seconds) => {
    if (videoRef.current && !videoRef.current.paused) {
      try { videoRef.current.pause(); } catch (_) { /* noop */ }
    }
    const at = Math.max(0, Number(seconds) || 0);
    setSourceModalAt(at);
    // Diligence signal (2026-07-20): opening the FULL original
    // recording was previously untracked — now stored + surfaced to
    // the seller (in-app notification, prefs-gated).
    beacon('source_open', { atSeconds: at });
  };

  const closeSourceModal = () => {
    setSourceModalAt(null);
    setSourceDuration(0);
  };

  // Close modal on Escape when open.
  useEffect(() => {
    if (sourceModalAt === null) return;
    const onKey = (e) => { if (e.key === 'Escape') closeSourceModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sourceModalAt]);

  // When the source modal opens, seek the video to the target timestamp
  // once metadata has loaded. If the browser already buffered the
  // metadata (cache hit), set it immediately.
  useEffect(() => {
    if (sourceModalAt === null) return;
    const v = sourceVideoRef.current;
    if (!v) return;
    const apply = () => {
      try { v.currentTime = sourceModalAt; } catch (_) { /* noop */ }
      v.play().catch(() => { /* user-gesture policies */ });
    };
    if (v.readyState >= 1 && v.duration) {
      apply();
    } else {
      v.addEventListener('loadedmetadata', apply, { once: true });
      return () => v.removeEventListener('loadedmetadata', apply);
    }
  }, [sourceModalAt]);

  const submitMessage = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!messageBody.trim()) {
      setMessageError('Please write a message before sending.');
      return;
    }
    setMessageSubmitting(true);
    setMessageError(null);
    try {
      const res = await fetch(`${API_BASE}/api/trailers/message/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: messageKind,
          senderName: messageName || null,
          senderEmail: messageEmail || null,
          body: messageBody,
          referencedClipIndex,
          website_url: messageHoneypot, // honeypot — bots fill, humans don't see
        }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to send message');
      }
      const data = await res.json().catch(() => ({}));
      setMessageBody('');
      // If we were already in a thread, just refresh it — the new
      // message appears inline above the form (no "Thanks" screen).
      if (threadKey && threadSig) {
        fetchThread();
        return;
      }
      // Otherwise this was the prospect's FIRST message. The backend
      // returned a threadKey + signature; flip into thread mode so the
      // just-sent message is visible and any future seller replies land
      // in the same conversation. Also update the URL so a page refresh
      // preserves thread state.
      if (data.threadKey && data.threadSig) {
        setThreadKey(data.threadKey);
        setThreadSig(data.threadSig);
        try {
          const params = new URLSearchParams(window.location.search);
          params.set('t', data.threadKey);
          params.set('s', data.threadSig);
          const clean = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
          window.history.replaceState({}, '', clean);
        } catch (_) { /* non-fatal */ }
        return;
      }
      // Fallback: server didn't mint a thread key (e.g. JWT_SECRET
      // missing). Keep the V1 "Thanks" behavior so the user still
      // sees confirmation.
      setMessageSent(true);
    } catch (err) {
      setMessageError(err.message);
    } finally {
      setMessageSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="share-viewer">
        <div className="share-viewer__loading">
          <div className="share-viewer__spinner" />
          <p>Loading your trailer…</p>
        </div>
      </div>
    );
  }

  if (processingStatus) {
    // The link is valid but the trailer hasn't finished rendering.
    // Distinct from the dead-link error: reassure + auto-refresh (the
    // repoll effect swaps this for the player when compose completes).
    return (
      <div className="share-viewer">
        <div className="share-viewer__loading" role="status" aria-live="polite">
          <div className="share-viewer__spinner" />
          <h1 className="share-viewer__processing-title">Your trailer is almost ready</h1>
          <p>
            The highlight reel is still being generated
            {processingStatus === 'processing' || processingStatus === 'analyzing'
              ? ' — usually just a few minutes.'
              : '.'}
          </p>
          <p className="share-viewer__processing-hint">
            This page checks automatically — no need to refresh.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="share-viewer">
        <div className="share-viewer__error">
          <h1>This share link isn't available</h1>
          <p>{error}</p>
          <p className="share-viewer__error-help">
            The link may have been revoked, or the recording is no longer available.
            Ask the sender to generate a new link.
          </p>
          <a className="share-viewer__footer-link" href="https://trailercast.io">
            Learn about TrailerCast →
          </a>
        </div>
      </div>
    );
  }

  const preparedFor = meta.stakeholderName
    ? `Prepared for ${meta.stakeholderName}`
    : 'Demo Highlights';

  // Byline shape, degrades cleanly based on which fields the seller has:
  //   name + title + company → "Jane Doe, Senior AE at Acme"
  //   name + title only       → "Jane Doe, Senior AE"
  //   name + company only     → "Jane Doe at Acme"  (legacy, pre-jobTitle)
  //   name only               → "Jane Doe"
  const byline = (() => {
    const parts = [meta.creatorName];
    if (meta.creatorJobTitle) parts.push(`, ${meta.creatorJobTitle}`);
    if (meta.creatorCompany) parts.push(` at ${meta.creatorCompany}`);
    return parts.join('');
  })();

  // Seller's initials fallback when no profile picture is set, so the
  // avatar circle always shows SOMETHING identifying.
  const initials = (meta.creatorName || 'TC')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('');

  // Inline CSS-var overrides let a seller's brand color tint the share
  // page without needing their own stylesheet. Falls back to the
  // default indigo when nothing is set.
  const containerStyle = meta.brandPrimaryColor
    ? { '--share-accent': meta.brandPrimaryColor }
    : undefined;

  return (
    <div
      className={`share-viewer${embedded ? ' share-viewer--embedded' : ''}`}
      style={containerStyle}
    >
      <div className="share-viewer__container">
        {/* Brand bar — company logo (when set) on the left, a tiny
            TrailerCast watermark on the right. Gives the page its
            "branded experience" feel before you hit the main title.
            Suppressed in embedded mode (host shell shows its own brand). */}
        {!embedded && meta.companyLogoUrl && (
          <div className="share-viewer__brandbar">
            <img
              className="share-viewer__brandbar-logo"
              src={meta.companyLogoUrl}
              alt={meta.creatorCompany || 'Company logo'}
            />
            {meta.creatorCompany && (
              <span className="share-viewer__brandbar-name">{meta.creatorCompany}</span>
            )}
          </div>
        )}

        {/* Seller header + "Prepared for" title — suppressed in
            embedded mode because the host shell (Decision Room header)
            already shows the seller branding + prospect company name.
            2026-07-18 hero redesign: compressed from a stacked block
            (48px avatar + 36px display title) into one slim row so the
            video — not the header — is the first thing that reads as
            "the page". */}
        {!embedded && (
          <header className="share-viewer__header share-viewer__header--slim">
            <div className="share-viewer__seller">
              {meta.creatorProfilePictureUrl ? (
                <img
                  className="share-viewer__seller-avatar"
                  src={meta.creatorProfilePictureUrl}
                  alt={meta.creatorName || 'Sender avatar'}
                />
              ) : (
                <div
                  className="share-viewer__seller-avatar share-viewer__seller-avatar--fallback"
                  aria-hidden="true"
                >
                  {initials}
                </div>
              )}
              <div className="share-viewer__seller-meta">
                <span className="share-viewer__seller-label">Prepared by</span>
                <span className="share-viewer__seller-name">{byline}</span>
              </div>
            </div>
            <h1 className="share-viewer__title share-viewer__title--slim">{preparedFor}</h1>
          </header>
        )}

        {/* Forwarded-from banner — when this distribution was itself a
            forward from a champion, we surface that provenance so the
            viewer understands who handed them the demo. Keeps the
            social-proof chain visible without burying the seller's own
            branding at the top of the page. Suppressed in embedded
            mode — Decision Room view shows its own forwarded banner. */}
        {!embedded && meta.forwardedFrom && meta.forwardedFrom.name && (
          <div className="share-viewer__forwarded-banner">
            <strong>{meta.forwardedFrom.name}</strong> shared this with you from {meta.creatorName || 'the sender'}
            {meta.creatorCompany ? ` at ${meta.creatorCompany}` : ''}.
          </div>
        )}

        {/* ─── Theater hero (2026-07-18) ──────────────────────────
            The player is the page's hero: a dark full-bleed band
            (standalone mode) that the 16:9 player sits centered in,
            with the chapter strip + player bar (current chapter title,
            speed control) living INSIDE the band so the whole viewing
            surface reads as one cinematic unit. Embedded mode (Decision
            Room) skips the band — the host shell owns the backdrop —
            but keeps the player bar + strip. */}
        <div className={`share-viewer__theater${embedded ? ' share-viewer__theater--embedded' : ''}`}>
          <div className="share-viewer__theater-inner">
            <div className="share-viewer__player-wrapper" style={{ position: 'relative' }}>
              <video
                ref={videoRef}
                className="share-viewer__player"
                controls
                playsInline
                preload="metadata"
                src={mainVideoUrl}
                aria-label={`Demo highlight trailer${meta.stakeholderName ? ` prepared for ${meta.stakeholderName}` : ''}`}
              >
                Your browser doesn't support HTML5 video.
              </video>
              {trailerOutage.outage && (
                <VideoOutageOverlay
                  secondsLeft={trailerOutage.secondsLeft}
                  message={trailerOutage.outage.message}
                  onRetry={trailerOutage.retry}
                />
              )}
            </div>

            {/* Color-coded chapter strip — same component the seller-
                facing surfaces use. Click seeks; [ / ] keys jump between
                chapters; active segment highlights. Self-renders null on
                demos that lack reelOffsetSeconds (legacy / pre-Phase-2D).
                (The old redundant tick-marker bar below it was removed in
                the 2026-07-18 hero redesign — both gated on the same
                reelOffsetSeconds field, so nothing is lost for legacy.) */}
            {videoDuration > 0 && Array.isArray(meta?.moments) && (
              <div className="share-viewer__theater-strip">
                <TopicChapterStrip
                  segments={meta.moments
                    .filter((m) => typeof m.reelOffsetSeconds === 'number')
                    .map((m) => {
                      // Each clip's trailer-timeline width = its source-
                      // recording duration (endSeconds - startSeconds).
                      // Falls back to 8s if either is missing — enough
                      // to give the segment a clickable footprint without
                      // visually swallowing neighbors.
                      const dur = (typeof m.endSeconds === 'number'
                          && typeof m.startSeconds === 'number')
                        ? Math.max(2, m.endSeconds - m.startSeconds)
                        : 8;
                      return {
                        startSeconds: m.reelOffsetSeconds,
                        endSeconds: m.reelOffsetSeconds + dur,
                        label: m.feature || 'Moment',
                      };
                    })}
                  totalDurationSeconds={videoDuration}
                  onSeek={(seconds) => jumpTo({ reelOffsetSeconds: seconds })}
                  videoRef={videoRef /* active-segment highlight + [/] keyboard nav */}
                  showLegend={false /* the chapter list further down already serves this role */}
                />
              </div>
            )}

            {/* Player bar — current chapter (live region so screen
                readers hear chapter changes) + playback speed. Native
                <video> controls keep play/pause/scrub/fullscreen. */}
            <div className="share-viewer__player-bar">
              <span className="share-viewer__now-playing" aria-live="polite">
                {playheadMomentIndex != null
                  && Array.isArray(meta?.moments)
                  && meta.moments[playheadMomentIndex]
                  ? (
                    <>
                      <span className="share-viewer__now-playing-label">Now playing</span>
                      <span className="share-viewer__now-playing-title">
                        {meta.moments[playheadMomentIndex].feature || `Chapter ${playheadMomentIndex + 1}`}
                      </span>
                    </>
                  ) : (
                    <span className="share-viewer__now-playing-label">
                      {Array.isArray(meta?.moments) && meta.moments.length > 0
                        ? `${meta.moments.length} chapters`
                        : 'Demo highlights'}
                    </span>
                  )}
              </span>
              <button
                type="button"
                className="share-viewer__speed-btn"
                onClick={cyclePlaybackRate}
                aria-label={`Playback speed ${playbackRate}x — click to change`}
                title="Playback speed"
              >
                {playbackRate}×
              </button>
            </div>
          </div>
        </div>

        {/* Two-column layout on desktop, BELOW the hero. Mobile: single
            column stack via CSS — the visual order matches the pre-grid
            layout (video → chapters → chat) because the grid-wrap is
            transparent below the breakpoint. Desktop: chapters left,
            chat sidebar right. Chat sticks to the viewport so the
            prospect can type while scrubbing the player. The theater
            band deliberately sits OUTSIDE this grid so the hero spans
            the full card width instead of being confined to the main
            column (the 2026-07-18 redesign's whole point). */}
        <div className="share-viewer__grid">
          <div className="share-viewer__main">

        {Array.isArray(meta.moments) && meta.moments.length > 0 && (
          <section className="share-viewer__chapters">
            <h2 className="share-viewer__chapters-title">In this trailer</h2>
            {/* Phase 2D: surface the full-recording affordance explicitly.
                Without this banner, the per-chapter "Watch full segment"
                pills were getting missed — users expected the feature
                to be announced. */}
            {meta.sourceAvailable ? (
              <p className="share-viewer__chapters-hint">
                Want more context on a clip? Hit <strong>Watch full segment</strong>
                on any chapter below to open the complete recording at that moment.
              </p>
            ) : (
              <p className="share-viewer__chapters-hint share-viewer__chapters-hint--muted">
                Full-recording replay isn't available for this trailer
                (uploaded before that feature shipped). Trailers generated
                from now on will have the option to jump into the
                original recording from any clip.
              </p>
            )}
            <ol className="share-viewer__chapters-list">
              {meta.moments.map((m, i) => {
                // Source end-timestamp mirrors the pill baked into the
                // trailer ("JUMP TO X:XX" = clip's end in the original
                // recording). Clicking "Watch full segment" opens the
                // source there so the viewer sees what happened next.
                const sourceJumpTo = typeof m.endSeconds === 'number'
                  ? m.endSeconds
                  : (typeof m.startSeconds === 'number' ? m.startSeconds : 0);
                return (
                  <li key={i} className="share-viewer__chapter">
                    <button
                      type="button"
                      className="share-viewer__chapter-button"
                      onClick={() => jumpTo(m)}
                    >
                      <span className="share-viewer__chapter-index">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="share-viewer__chapter-body">
                        <span className="share-viewer__chapter-feature">{m.feature}</span>
                        {m.benefit && (
                          <span className="share-viewer__chapter-benefit">{m.benefit}</span>
                        )}
                      </span>
                      <span className="share-viewer__chapter-time">
                        {formatClipTime(
                          typeof m.reelOffsetSeconds === 'number'
                            ? m.reelOffsetSeconds
                            : m.startSeconds
                        )}
                      </span>
                    </button>
                    {meta.sourceAvailable && (
                      <button
                        type="button"
                        className="share-viewer__chapter-source"
                        onClick={(e) => { e.stopPropagation(); openSourceAt(sourceJumpTo); }}
                        title={`Watch the full recording starting at ${formatClipTime(sourceJumpTo)}`}
                      >
                        Watch full segment
                        <span className="share-viewer__chapter-source-time">
                          {formatClipTime(sourceJumpTo)}
                        </span>
                      </button>
                    )}
                  </li>
                );
              })}
            </ol>
          </section>
        )}

        {/* CTA — placed AFTER the chapters and stuck to the viewport
            bottom (2026-07-18): while the buyer scrolls the chapter
            list, the next step floats at the bottom edge, then settles
            into its natural resting place at the end of the column.
            Position-after-content is what makes `position: sticky;
            bottom` produce the floating behavior — an element earlier
            in the flow would never engage. */}
        {meta.cta && meta.cta.url && (
          <div className="share-viewer__cta-row share-viewer__cta-row--sticky">
            <a
              className="share-viewer__cta-button"
              href={meta.cta.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onCtaClick}
            >
              {meta.cta.label || 'Learn more'}
              <span className="share-viewer__cta-arrow">→</span>
            </a>
          </div>
        )}

          </div>{/* /.share-viewer__main */}

          <aside className="share-viewer__side">

        {/* Seller note — demoted from the top of the page into the
            sidebar (2026-07-18 hero redesign) so it no longer pushes
            the video down. Collapsible-open: the note is context, not
            the main event, but hiding it entirely would waste the
            seller's personalization effort. Native <details> = free
            keyboard + screen-reader semantics, no new components. */}
        {meta.sellerNote && (
          <details className="share-viewer__side-card share-viewer__note-card" open>
            <summary className="share-viewer__side-card-summary">
              A note from {meta.creatorName ? meta.creatorName.split(' ')[0] : 'the sender'}
            </summary>
            <p className="share-viewer__note-card-body">{meta.sellerNote}</p>
          </details>
        )}

        {/* Engagement form — prospect can send the seller a question,
            a comment, or a follow-up request without creating an account.
            One form, three kinds via segmented-button selector. In V2
            thread-resume mode, the active conversation is rendered
            above the form and the form becomes a reply box. */}
        <section className="share-viewer__engage" aria-labelledby="engage-title">
          <h2 id="engage-title" className="share-viewer__engage-title">
            {threadMessages && threadMessages.length > 0
              ? `Conversation with ${meta.creatorName ? meta.creatorName.split(' ')[0] : 'the sender'}`
              : `Message ${meta.creatorName ? meta.creatorName.split(' ')[0] : 'the sender'}`}
          </h2>
          <p className="share-viewer__engage-sub">
            {threadMessages && threadMessages.length > 0
              ? 'Your reply will keep this conversation going — they\'ll be notified right away.'
              : 'Have a question, want to leave a comment, or ready for next steps? Send it directly — replies come straight from them by email.'}
          </p>

          {/* Thread transcript — rendered when resume params are present. */}
          {threadLoading && (
            <div className="share-viewer__thread-loading">Loading conversation…</div>
          )}
          {threadError && !threadLoading && (
            <div className="share-viewer__engage-error">
              Couldn't load the conversation: {threadError}
            </div>
          )}
          {threadMessages && threadMessages.length > 0 && (
            <ol className="share-viewer__thread-list">
              {threadMessages.map((m) => {
                const senderLabel = m.senderKind === 'seller'
                  ? (m.senderName || (meta.creatorName || 'Sender'))
                  : (m.senderName || 'You');
                const canDelete = threadIsOwner && m.senderKind === 'seller';
                return (
                  <li
                    key={m.id}
                    className={`share-viewer__thread-msg is-${m.senderKind} ${canDelete ? 'can-delete' : ''}`}
                  >
                    <div className="share-viewer__thread-msg-head">
                      <strong>{senderLabel}</strong>
                      <span className="share-viewer__thread-msg-date">
                        {new Date(m.createdAt).toLocaleString(undefined, {
                          month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                        })}
                      </span>
                      {typeof m.referencedClipIndex === 'number' && (
                        <span
                          title="This message refers to a specific clip"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            marginLeft: 6, padding: '2px 8px',
                            borderRadius: 100, fontSize: 11, fontWeight: 600,
                            background: 'rgba(99, 102, 241, 0.10)',
                            color: '#4f46e5',
                            border: '1px solid rgba(99, 102, 241, 0.22)',
                          }}
                        >
                          Re: {m.referencedClipFeature || `Clip ${m.referencedClipIndex + 1}`}
                        </span>
                      )}
                    </div>
                    <div className="share-viewer__thread-msg-body">
                      {m.body}
                      {canDelete && (
                        <button
                          type="button"
                          className="share-viewer__thread-msg-delete"
                          onClick={() => deleteOwnMessage(m.id)}
                          title="Delete this message"
                          aria-label="Delete message"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          {threadIsOwner ? (
            // Seller-reply mode: authenticated owner of the demo landed
            // here via an email deep-link. Swap the prospect form for a
            // seller-reply box so the share page is the communication
            // hub for both sides of the conversation.
            <form className="share-viewer__seller-reply" onSubmit={submitSellerReply}>
              <div className="share-viewer__seller-reply-banner">
                You're viewing this as the trailer's owner. Your reply will be
                sent as <strong>{meta.creatorName || 'you'}</strong> and the
                prospect will get an email notification.
              </div>
              <label className="share-viewer__engage-body">
                <span>Your reply</span>
                <textarea
                  value={sellerReplyBody}
                  onChange={(e) => setSellerReplyBody(e.target.value)}
                  placeholder="Write your response here…"
                  rows={5}
                  maxLength={4000}
                  required
                />
              </label>
              {sellerReplyError && (
                <div className="share-viewer__engage-error">{sellerReplyError}</div>
              )}
              <button
                type="submit"
                className="share-viewer__engage-submit"
                disabled={sellerReplySubmitting || !sellerReplyBody.trim()}
              >
                {sellerReplySubmitting ? 'Sending…' : 'Send reply'}
              </button>
            </form>
          ) : messageSent ? (
            <div className="share-viewer__engage-done">
              <strong>Thanks — {meta.creatorName ? meta.creatorName.split(' ')[0] : 'they'} will reach out shortly.</strong>
              <p>You'll hear back at {messageEmail || 'the email you provided'}.</p>
              <button
                type="button"
                className="share-viewer__engage-again"
                onClick={() => { setMessageSent(false); setMessageBody(''); }}
              >
                Send another message
              </button>
            </div>
          ) : (
            <form className="share-viewer__engage-form" onSubmit={submitMessage}>
              {/* Kind picker hidden in thread-reply mode — replies carry
                  forward the kind of the original message. */}
              {!(threadMessages && threadMessages.length > 0) && (
                <div className="share-viewer__engage-kinds" role="radiogroup" aria-label="Message type">
                  {[
                    { value: 'question', label: 'Ask a question' },
                    { value: 'comment', label: 'Leave a comment' },
                    { value: 'followup_request', label: 'Request a follow-up' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={messageKind === opt.value}
                      className={`share-viewer__engage-kind ${messageKind === opt.value ? 'is-selected' : ''}`}
                      onClick={() => setMessageKind(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="share-viewer__engage-row">
                <label>
                  <span>Your name</span>
                  <input
                    type="text"
                    value={messageName}
                    onChange={(e) => setMessageName(e.target.value)}
                    placeholder="Jane Doe"
                    maxLength={120}
                    autoComplete="name"
                  />
                </label>
                <label>
                  <span>Email</span>
                  <input
                    type="email"
                    value={messageEmail}
                    onChange={(e) => setMessageEmail(e.target.value)}
                    placeholder="you@company.com"
                    maxLength={255}
                    autoComplete="email"
                  />
                </label>
              </div>

              {/* Clip-reference picker. Lets the prospect tag the
                  message with which clip they're asking about, so the
                  seller's inbox shows "Re: <feature>" alongside the
                  message body. Defaults to whichever clip the playhead
                  is over; "General" sends untagged. Hidden when the
                  trailer has no moments to choose from. */}
              {Array.isArray(meta?.moments) && meta.moments.length > 0 && (
                <label className="share-viewer__engage-body">
                  <span>Re: a specific clip? (optional)</span>
                  <select
                    value={referencedClipIndex == null ? '' : String(referencedClipIndex)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setClipPickTouched(true);
                      setReferencedClipIndex(v === '' ? null : Number(v));
                    }}
                    style={{
                      width: '100%', padding: '8px 10px',
                      border: '1px solid #cbd5e1', borderRadius: 8,
                      fontSize: 14, fontFamily: 'inherit',
                      background: '#fff', boxSizing: 'border-box',
                    }}
                  >
                    <option value="">General — not about a specific clip</option>
                    {meta.moments.map((m, i) => (
                      <option key={i} value={String(i)}>
                        Clip {i + 1}{m.feature ? ` — ${m.feature}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="share-viewer__engage-body">
                <span>Message</span>
                <textarea
                  value={messageBody}
                  onChange={(e) => setMessageBody(e.target.value)}
                  placeholder={
                    messageKind === 'question' ? "What's your question about the trailer?"
                    : messageKind === 'followup_request' ? "Let them know when / how you'd like to connect."
                    : "What stood out? Anything specific you'd like to discuss?"
                  }
                  rows={5}
                  maxLength={4000}
                  required
                />
              </label>

              {/* Honeypot — hidden from real users via CSS; bots fill it. */}
              <label className="share-viewer__engage-hp" aria-hidden="true">
                Website
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={messageHoneypot}
                  onChange={(e) => setMessageHoneypot(e.target.value)}
                />
              </label>

              {messageError && (
                <div className="share-viewer__engage-error">{messageError}</div>
              )}

              <button
                type="submit"
                className="share-viewer__engage-submit"
                disabled={messageSubmitting || !messageBody.trim()}
              >
                {messageSubmitting ? 'Sending…' : 'Send'}
              </button>
            </form>
          )}
        </section>

        {/* Champion-forwarding card. The sales-engagement thesis here:
            the person who watches the demo is rarely the sole decision
            maker — they need to rope in a CFO, CISO, Legal, etc. Give
            them a friction-free "forward this" affordance and the
            seller sees a branching tree of everyone inside the buyer's
            org who engaged. This is how a single shared trailer becomes
            a multi-threaded deal. */}
        {/* Collapsed by default since the 2026-07-18 redesign — the
            forward card is a power feature, not the next step; keeping
            it closed slims the sidebar so the conversation + CTA stay
            dominant. <details> auto-opens when a forward is in flight
            (forwardResult) via the `open` prop so post-submit state
            never hides itself. */}
        <details
          className="share-viewer__engage share-viewer__forward share-viewer__forward--collapsible"
          open={Boolean(forwardResult) || undefined}
        >
          <summary className="share-viewer__side-card-summary share-viewer__forward-summary">
            Share this with your team
          </summary>
          <p className="share-viewer__engage-sub">
            Forward this demo to a teammate — your CFO, your CISO, anyone who needs
            to see it. We'll give you a personalized link so you can track who watched.
          </p>

          {forwardResult ? (
            // Post-submit: lead with "Open in email" — a mailto: link
            // that prefills the champion's default mail client (Gmail,
            // Outlook, Apple Mail) with To/Subject/Body. The email
            // sends from THEIR real inbox, which is essential for
            // deliverability to C-suite recipients (and threading on
            // replies). Copy-link stays as a secondary action for
            // Slack / Teams / etc.
            <div className="share-viewer__forward-done">
              <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                Ready to send to {forwardResult.recipientName}
                {forwardResult.reused && (
                  <span style={{ color: '#64748b', fontWeight: 400, fontSize: 13 }}>
                    {' '}(link reused — they already had one)
                  </span>
                )}
              </div>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                Sending from your own inbox lands far more reliably than from us. One click below opens your email client with everything ready to go.
              </p>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <a
                  href={buildMailtoUrl({
                    to: forwardResult.recipientEmail,
                    subject: 'Quick demo recap',
                    body: forwardResult.preWrittenMessage,
                  })}
                  style={{
                    flex: '1 1 auto',
                    minWidth: 200,
                    padding: '11px 18px',
                    borderRadius: 10,
                    background: '#4f46e5',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600,
                    textAlign: 'center',
                    textDecoration: 'none',
                    fontFamily: 'inherit',
                    boxShadow: '0 2px 8px rgba(79, 70, 229, 0.25)',
                  }}
                >
                  📧  Open in email
                </a>
                <button
                  type="button"
                  onClick={() => copyToClipboard(forwardResult.url, 'url')}
                  style={{
                    padding: '11px 16px',
                    borderRadius: 10,
                    background: '#fff',
                    border: '1px solid #cbd5e1',
                    color: '#334155',
                    fontSize: 14, fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {forwardCopied.url ? '✓ Link copied' : 'Copy link'}
                </button>
              </div>

              <details style={{ marginTop: 14 }}>
                <summary style={{
                  cursor: 'pointer',
                  fontSize: 13,
                  color: '#64748b',
                  userSelect: 'none',
                }}>
                  Preview the message
                </summary>
                <div style={{
                  marginTop: 10,
                  padding: '12px 14px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: 13,
                  color: '#334155',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.5,
                }}>
                  {forwardResult.preWrittenMessage}
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(forwardResult.preWrittenMessage, 'message')}
                  style={{
                    marginTop: 8,
                    padding: '7px 12px',
                    borderRadius: 8,
                    background: '#fff',
                    border: '1px solid #cbd5e1',
                    color: '#334155',
                    fontSize: 12, fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {forwardCopied.message ? '✓ Copied' : 'Copy message text'}
                </button>
              </details>

              <button
                type="button"
                className="share-viewer__engage-again"
                onClick={resetForwardForm}
                style={{ marginTop: 12 }}
              >
                Forward another teammate
              </button>
            </div>
          ) : (
            <form className="share-viewer__engage-form" onSubmit={submitForward}>
              <div className="share-viewer__engage-row">
                <label>
                  <span>Teammate's first name</span>
                  <input
                    type="text"
                    value={forwardFirstName}
                    onChange={(e) => setForwardFirstName(e.target.value)}
                    placeholder="David"
                    maxLength={120}
                    required
                  />
                </label>
                <label>
                  <span>Teammate's email</span>
                  <input
                    type="email"
                    value={forwardEmail}
                    onChange={(e) => setForwardEmail(e.target.value)}
                    placeholder="david@company.com"
                    maxLength={255}
                    required
                  />
                </label>
              </div>
              <label className="share-viewer__engage-body">
                <span>Personal note (optional)</span>
                <textarea
                  value={forwardNote}
                  onChange={(e) => setForwardNote(e.target.value)}
                  placeholder="Worth 5 min — this is what I was telling you about"
                  rows={3}
                  maxLength={1000}
                />
              </label>
              {forwardError && (
                <div className="share-viewer__engage-error">{forwardError}</div>
              )}
              <button
                type="submit"
                className="share-viewer__engage-submit"
                disabled={forwardSubmitting || !forwardFirstName.trim() || !forwardEmail.trim()}
              >
                {forwardSubmitting ? 'Creating link…' : 'Get link'}
              </button>
            </form>
          )}

          {/* Teammates this champion has already forwarded to. Renders
              inside the same card so the engagement feedback loop is
              right next to the action — forward → watch who watched →
              follow up. This is the champion's own mini-dashboard. */}
          {Array.isArray(meta.forwardedChildren) && meta.forwardedChildren.length > 0 && (
            <div className="share-viewer__forward-children">
              <h3 className="share-viewer__forward-children-title">Teammates you've shared with</h3>
              <ul className="share-viewer__forward-children-list">
                {meta.forwardedChildren.map((child) => {
                  const opened = (child.viewCount || 0) > 0;
                  return (
                    <li key={child.distributionId} className="share-viewer__forward-child">
                      <span className="share-viewer__forward-child-icon" aria-hidden="true">✉️</span>
                      <div className="share-viewer__forward-child-info">
                        <span className="share-viewer__forward-child-name">
                          {child.recipientName || child.recipientEmail}
                        </span>
                        <span className="share-viewer__forward-child-email">
                          {child.recipientEmail}
                        </span>
                      </div>
                      <span className="share-viewer__forward-child-meta">
                        {opened
                          ? `Watched ${child.viewCount}x${child.lastViewedAt ? ` · Last opened ${formatTimeAgo(child.lastViewedAt)}` : ''}`
                          : 'Not opened yet'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </details>

          </aside>{/* /.share-viewer__side */}
        </div>{/* /.share-viewer__grid */}

        <footer className="share-viewer__footer">
          <div className="share-viewer__footer-brand">
            <span className="share-viewer__footer-logo">TrailerCast</span>
            <span className="share-viewer__footer-tagline">AI-powered sales demo highlights</span>
          </div>
          <a
            className="share-viewer__footer-link"
            href="https://trailercast.io?utm_source=share-page"
            target="_blank"
            rel="noopener noreferrer"
          >
            Create your own →
          </a>
        </footer>

        <p className="share-viewer__privacy">
          This page is personalized by {meta.creatorName}
          {meta.creatorCompany ? ` at ${meta.creatorCompany}` : ''}.
          Playback analytics (watch time, completion) are recorded so they
          can follow up meaningfully. We don't store your identity.
        </p>
      </div>

      {/* Phase 2D: source-recording deep-link modal. Opens when the
          viewer clicks "Watch full segment" on a chapter. The <video>
          seeks to sourceModalAt once metadata loads (see effect above). */}
      {sourceModalAt !== null && (
        <div
          className="share-viewer__source-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="source-modal-title"
          onClick={closeSourceModal}
        >
          <div className="share-viewer__source-modal" onClick={(e) => e.stopPropagation()}>
            <div className="share-viewer__source-modal-head">
              <div className="share-viewer__source-modal-titles">
                <h3 id="source-modal-title">Full Demo Recording</h3>
                <span className="share-viewer__source-modal-sub">
                  The original, unedited meeting — starting at {formatClipTime(sourceModalAt)}
                  {sourceDuration > 0 && <> of {formatClipTime(sourceDuration)}</>}
                </span>
              </div>
              <button
                type="button"
                className="share-viewer__source-modal-close"
                onClick={closeSourceModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div style={{ position: 'relative' }}>
              <video
                ref={sourceVideoRef}
                className="share-viewer__source-modal-video"
                controls
                playsInline
                preload="metadata"
                src={sourceUrl}
                onLoadedMetadata={(e) => setSourceDuration(e.target.duration || 0)}
              >
                Your browser doesn't support HTML5 video.
              </video>
              {sourceOutage.outage && (
                <VideoOutageOverlay
                  secondsLeft={sourceOutage.secondsLeft}
                  message={sourceOutage.outage.message}
                  onRetry={sourceOutage.retry}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
